// Dérivations « métier » à partir de l'état et du contenu : session suivante, semaine
// de programme, régularité, file de révision, résumé pour le tableau de bord et le mentor.
// Fonctions pures, utilisées par le navigateur ET par le serveur (API mentor).
import type { Etat, EtatCarte } from './types';
import type { BundleParcours, BundleSaison, NotionCompilee, SessionCompilee } from './contenu';
import { estDue, estNouvelle } from './sm2';
import { ajouterJours, jourIso, semaineIso, semainesDepuis, lundiDe } from './dates';

/**
 * Toutes les sessions d'une SAISON, dans l'ordre du programme.
 * R-PAR-03 : les sessions de parcours annexe n'y figurent jamais — c'est ce qui garantit
 * qu'un parcours ne décale pas `sessionSuivante()` ni rien de ce qui en dérive (ADR 0009).
 */
export function sessionsDe(bundle: BundleSaison): SessionCompilee[] {
  return bundle.semaines.flatMap((s) => s.sessions);
}

/**
 * Le MÉMO (R-MEM-02) : toutes les notions enseignées jusqu'ici, la plus récente d'abord.
 * `seulementVues` ne garde que celles des séances terminées — le mémo ne doit rien révéler
 * d'une séance qu'on n'a pas encore faite.
 */
export function memo(
  etat: Etat,
  bundles: BundleSaison[],
  parcours: BundleParcours[] = [],
  seulementVues = true,
): NotionCompilee[] {
  const sessions = [
    ...bundles.flatMap(sessionsDe),
    ...parcours.filter((p) => etat.parcours[p.id]).flatMap((p) => p.sessions),
  ];
  return sessions
    .filter((s) => !seulementVues || etat.progression.sessions[s.id]?.statut === 'terminee')
    .flatMap((s) => s.notions)
    .reverse();
}

/** Filtre le mémo sur un texte libre, accents et casse ignorés. */
export function chercherDansLeMemo(notions: NotionCompilee[], recherche: string): NotionCompilee[] {
  const normaliser = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const terme = normaliser(recherche.trim());
  if (!terme) return notions;
  return notions.filter((n) => normaliser(n.recherche).includes(terme));
}

/** La première session non terminée d'un parcours annexe (R-PAR-02). */
export function sessionSuivanteDeParcours(etat: Etat, parcours: BundleParcours): SessionCompilee | undefined {
  return parcours.sessions.find((s) => etat.progression.sessions[s.id]?.statut !== 'terminee');
}

/** Avancement d'un parcours annexe : sessions terminées sur total. */
export function avancementDeParcours(etat: Etat, parcours: BundleParcours): { faites: number; total: number } {
  const faites = parcours.sessions.filter((s) => etat.progression.sessions[s.id]?.statut === 'terminee').length;
  return { faites, total: parcours.sessions.length };
}

/** La session suivante = la première non terminée dans l'ordre (R-PRG-01). */
export function sessionSuivante(etat: Etat, bundle: BundleSaison): SessionCompilee | undefined {
  return sessionsDe(bundle).find((s) => etat.progression.sessions[s.id]?.statut !== 'terminee');
}

/** La session commencée et non terminée, s'il y en a une (bouton « Reprendre », R-SES-06). */
export function sessionEnCours(etat: Etat, bundle: BundleSaison): SessionCompilee | undefined {
  return sessionsDe(bundle).find((s) => etat.progression.sessions[s.id]?.statut === 'en_cours');
}

/** Semaine de programme en cours = celle de la session suivante (R-PRG-02) ; 11 si tout est fini. */
export function semaineDeProgramme(etat: Etat, bundle: BundleSaison): number {
  return sessionSuivante(etat, bundle)?.semaine ?? 11;
}

/**
 * Progression par domaine : sessions terminées / sessions portant le domaine (R-TDB-03).
 * R-PAR-04 : les parcours annexes **activés** y sont comptés — c'est du travail réel, et c'est
 * le seul endroit du tableau de bord où l'électronique reste visible depuis l'ADR 0009.
 */
export function progressionParDomaine(etat: Etat, bundle: BundleSaison, parcours: BundleParcours[] = []): Record<string, { faites: number; total: number }> {
  const resultat: Record<string, { faites: number; total: number }> = {};
  const actifs = parcours.filter((p) => etat.parcours[p.id]).flatMap((p) => p.sessions);
  for (const s of [...sessionsDe(bundle), ...actifs]) {
    const terminee = etat.progression.sessions[s.id]?.statut === 'terminee';
    for (const d of s.domaines) {
      const r = (resultat[d] ??= { faites: 0, total: 0 });
      r.total += 1;
      if (terminee) r.faites += 1;
    }
  }
  return resultat;
}

export type StatutSemaine = 'complete' | 'partielle' | 'vide' | 'off' | 'future';

export interface SemaineRegularite {
  semaine: string;
  numero: number;
  sessions: number;
  defi: boolean;
  statut: StatutSemaine;
}

/**
 * Grille de régularité des 13 semaines d'une saison (R-PLA-04, R-TDB-04).
 * `aujourdhui` au format AAAA-MM-JJ.
 */
export function grilleRegularite(etat: Etat, saison: number, aujourdhui: string): SemaineRegularite[] {
  const debut = etat.parametres.debut_saisons[String(saison)];
  if (!debut) return [];
  const semaines = semainesDepuis(debut, 13);
  const semaineActuelle = semaineIso(aujourdhui);
  const parSemaine = new Map<string, number>();
  for (const r of etat.progression.realisations) {
    const s = semaineIso(r.le.slice(0, 10));
    parSemaine.set(s, (parSemaine.get(s) ?? 0) + 1);
  }
  const defisFaits = new Set(
    Object.values(etat.progression.defis).filter((d) => d.statut === 'fait' && d.fait_le).map((d) => semaineIso(d.fait_le!.slice(0, 10))),
  );
  return semaines.map((semaine, i) => {
    const sessions = parSemaine.get(semaine) ?? 0;
    let statut: StatutSemaine;
    if (i === 12 || etat.semaines_off.includes(semaine)) statut = 'off';
    else if (semaine > semaineActuelle) statut = 'future';
    else if (sessions >= 3) statut = 'complete';
    else if (sessions >= 1) statut = 'partielle';
    else statut = 'vide';
    return { semaine, numero: i + 1, sessions, defi: defisFaits.has(semaine), statut };
  });
}

/** Nombre de semaines « complètes » d'affilée en remontant depuis la semaine passée (les off ne cassent pas la série). */
export function serieRegularite(grille: SemaineRegularite[], aujourdhui: string): number {
  const semaineActuelle = semaineIso(aujourdhui);
  let serie = 0;
  const passees = grille.filter((s) => s.semaine < semaineActuelle).reverse();
  for (const s of passees) {
    if (s.statut === 'off') continue;
    if (s.statut === 'complete') serie += 1;
    else break;
  }
  // La semaine en cours compte si elle est déjà complète.
  const actuelle = grille.find((s) => s.semaine === semaineActuelle);
  if (actuelle?.statut === 'complete') serie += 1;
  return serie;
}

/** Prochain créneau planifié à partir d'aujourd'hui (R-PLA-02) ; aujourd'hui inclus si rien n'a été fait aujourd'hui. */
export function prochainCreneau(etat: Etat, aujourdhui: string): string {
  const faitAujourdhui = etat.progression.realisations.some((r) => r.le.slice(0, 10) === aujourdhui);
  const jours = etat.parametres.jours.length ? etat.parametres.jours : [1, 3, 4];
  for (let i = faitAujourdhui ? 1 : 0; i < 14; i++) {
    const date = ajouterJours(aujourdhui, i);
    if (jours.includes(jourIso(date))) return date;
  }
  return ajouterJours(aujourdhui, 1);
}

/** File de révision (R-CAR-04) : cartes dues, puis au plus 20 nouvelles par jour. */
export function fileDeRevision(etat: Etat, aujourdhui: string, maxNouvelles = 20): string[] {
  const entrees = Object.entries(etat.cartes).filter(([, c]) => estDue(c, aujourdhui));
  const nouvellesDuJour = etat.revisions.filter((r) => r.le.slice(0, 10) === aujourdhui && etat.cartes[r.carte] && (etat.cartes[r.carte] as EtatCarte).repetitions <= 1).length;
  let budgetNouvelles = Math.max(0, maxNouvelles - nouvellesDuJour);
  const anciennes = entrees.filter(([, c]) => !estNouvelle(c)).sort(([, a], [, b]) => a.echeance.localeCompare(b.echeance) || a.actif_depuis.localeCompare(b.actif_depuis));
  const nouvelles = entrees.filter(([, c]) => estNouvelle(c)).sort(([, a], [, b]) => a.actif_depuis.localeCompare(b.actif_depuis));
  const file = anciennes.map(([id]) => id);
  for (const [id] of nouvelles) { if (budgetNouvelles-- <= 0) break; file.push(id); }
  return file;
}

/**
 * Les compétences qu'il faut signaler (R-VEI-02) : vues dans les trois dernières veilles et
 * jamais couvertes. Trois mois de suite, c'est un manque du programme, pas un hasard du marché.
 */
export function ecartsPersistants(etat: Etat): { competence: string; couverture: string; fois: number }[] {
  const trois = etat.veille.entrees.slice(-3);
  if (trois.length < 3) return [];
  const dates = trois.map((e) => e.le.slice(0, 10));
  return Object.entries(etat.veille.ecarts)
    .map(([competence, ecart]) => ({ competence, couverture: ecart.couverture, fois: dates.filter((d) => ecart.vu_le.includes(d)).length }))
    .filter((x) => x.fois >= 3 && x.couverture !== 'couverte')
    .sort((a, b) => a.competence.localeCompare(b.competence));
}

/** Nombre de sessions terminées aujourd'hui (R-PRG-07 : suggestion douce après 2). */
export function sessionsAujourdhui(etat: Etat, aujourdhui: string): number {
  return etat.progression.realisations.filter((r) => r.le.slice(0, 10) === aujourdhui).length;
}

/** Le défi de la semaine de programme en cours. */
export function defiDeLaSemaine(etat: Etat, bundle: BundleSaison) {
  const numero = semaineDeProgramme(etat, bundle);
  const semaine = bundle.semaines.find((s) => s.numero === numero);
  if (!semaine) return undefined;
  return { ...semaine.defi, etat: etat.progression.defis[semaine.defi.id]?.statut ?? 'propose' };
}

/** La prochaine soutenance non faite. */
export function prochaineSoutenance(etat: Etat, bundle: BundleSaison) {
  for (const s of bundle.semaines) {
    if (s.soutenance && etat.progression.soutenances[s.soutenance.id]?.statut !== 'faite') return { semaine: s.numero, ...s.soutenance };
  }
  return undefined;
}

/** Semaine calendaire de la saison (1 à 13) pour une date, ou undefined hors saison. */
export function numeroSemaineCalendaire(etat: Etat, saison: number, date: string): number | undefined {
  const debut = etat.parametres.debut_saisons[String(saison)];
  if (!debut) return undefined;
  const lundi = lundiDe(debut);
  const ecartJours = Math.round((Date.parse(date + 'T12:00:00Z') - Date.parse(lundi + 'T12:00:00Z')) / 86400000);
  if (ecartJours < 0) return undefined;
  const n = Math.floor(ecartJours / 7) + 1;
  return n <= 13 ? n : undefined;
}
